import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, IconButton, Button, Stack, Typography, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QRCodeCanvas } from 'qrcode.react';

interface QRCodeModalProps {
    open: boolean;
    onClose: () => void;
    couponId: string;
}

const QRCodeModal: React.FC<QRCodeModalProps> = ({ open, onClose, couponId }) => {
    const [showCopySuccess, setShowCopySuccess] = useState(false);
    const couponUrl = `${window.location.origin}/c/${couponId}`;

    const handleDownload = () => {
        const canvas = document.getElementById(`qr-canvas-${couponId}`) as HTMLCanvasElement;
        if (!canvas) return;
        
        // Create a temporary link
        const link = document.createElement('a');
        link.download = `qrcode-${couponId}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(couponUrl);
            setShowCopySuccess(true);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Campaign QR Code
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{ ml: 2 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        p: 3,
                        bgcolor: 'white' 
                    }}>
                        <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                            Share this QR code with customers. When scanned, they can enter their details to receive their personal coupon.
                        </Typography>
                        <Box sx={{ 
                            p: 3, 
                            bgcolor: 'white', 
                            borderRadius: 1,
                            boxShadow: 1
                        }}>
                            <QRCodeCanvas
                                id={`qr-canvas-${couponId}`}
                                value={couponUrl}
                                size={256}
                                level="H"
                                includeMargin
                                style={{ display: 'block' }}
                            />
                        </Box>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownload}
                            >
                                Download QR Code
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={handleCopyUrl}
                            >
                                Copy URL
                            </Button>
                        </Stack>
                    </Box>
                </DialogContent>
            </Dialog>
            <Snackbar
                open={showCopySuccess}
                autoHideDuration={3000}
                onClose={() => setShowCopySuccess(false)}
                message="URL copied to clipboard"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </>
    );
};

export default QRCodeModal;
